import { orderBy } from 'lodash'
import queryString from 'query-string'
import { useEffect, useState } from 'react'
import { Link, useNotify } from 'react-admin'
import { AddressFormat } from 'shared/components/Address/AddressFormat'
import { Grid } from 'shared/components/Base/Base'
import { Wrap } from 'shared/components/Base/Wrap'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { ErrorMessage } from 'shared/components/Message/ErrorMessage'
import { InlineSpinner } from 'shared/components/Spinner'
import { TaskStatusEnum } from 'shared/components/TaskList/TaskList'
import { Text } from 'shared/components/Text/Text'
import { TextAreaInput } from 'shared/components/TextInput/TextAreaInput'
import { Handler } from 'shared/helpers/typeHelper'
import { toCamelCase, toSnakeCase } from 'shared/helpers/utils'
import { useEsignLeaseAgreement } from 'shared/hooks/query/useEsignLeaseAgreement'
import { useStaffUsers } from 'shared/hooks/query/useStaffUsers'
import {
  createEsignLeaseAgreement,
  EsignEnvelopeStatus,
  EsignLeaseAgreement,
  previewEsignLeaseAgreement,
  sendEsignLeaseAgreement,
  voidEsignLeaseAgreement,
} from 'shared/models/esign'
import { OnboardingChecklist, OnboardingSubChecklist } from 'shared/models/onboarding'
import { StaffUser, User } from 'shared/models/user'
import { bgGray, darkGray, errorD1, lightGray1, midGray } from 'shared/theme/colors'

import { useTeam } from '../../helpers/useTeam'
import { useLoggedInUser } from '../../hooks/useLoggedInUser'
import { Team } from '../../models/team'
import TeamTaxId from '../Teams/TeamTaxId'
import UserSSN from './UserSSN'

interface UserOnboardingSignLeaseProps {
  user: User
  task: OnboardingChecklist | OnboardingSubChecklist
  refetchChecklist: () => Promise<any>
}

export function UserOnboardingSignLease({ task, user, refetchChecklist }: UserOnboardingSignLeaseProps) {
  const eventFromUrl = (queryString.parse(window.location.search).event as string) || ''
  const shouldTriggerSend = eventFromUrl.toLowerCase() === 'send'
  const [openVoidDialog, setOpenVoidDialog] = useState(false)
  const [openGenerateDialog, setOpenGenerateDialog] = useState(shouldTriggerSend)

  // User haven't filled driver application yet
  if (
    task.statusDetail === 'MISSING_INFORMATION_FOR_LEASE_AGREEMENT' ||
    task.statusDetail === 'WRONG_RECIPIENT_FOR_LEASE_AGREEMENT' ||
    !user.team
  )
    return null

  async function handleSuccess() {
    refetchChecklist()
    setOpenGenerateDialog(false)
    setOpenVoidDialog(false)
  }

  if (task.status === TaskStatusEnum.PREREQUISITES_NOT_MET) {
    return (
      <>
        {openGenerateDialog && (
          <SignLeaseAgreementGeneratorModal
            user={user}
            onClose={() => setOpenGenerateDialog(false)}
            onSuccess={handleSuccess}
            shouldTriggerSend={shouldTriggerSend}
          />
        )}
        <Button variant="primary" size="medium" onClick={() => setOpenGenerateDialog(true)}>
          Generate
        </Button>
      </>
    )
  }

  if (task.status === TaskStatusEnum.NOT_STARTED) {
    return (
      <>
        {openVoidDialog && (
          <SignLeaseAgreementVoidModal
            user={user}
            onClose={() => setOpenVoidDialog(false)}
            onSuccess={handleSuccess}
          />
        )}
        <Button variant="primary" size="medium" onClick={() => setOpenVoidDialog(true)}>
          Void
        </Button>
      </>
    )
  }

  return null
}

interface SignLeaseAgreementGeneratorModalProps {
  user: User
  onClose: Handler
  onSuccess: () => Promise<any>
  shouldTriggerSend?: boolean
}

function SignLeaseAgreementGeneratorModal({
  onClose,
  onSuccess,
  user,
  shouldTriggerSend,
}: SignLeaseAgreementGeneratorModalProps) {
  const team = useTeam(user?.team)
  const {
    esignLeaseAgreement,
    isLoading: isLeaseAgreementLoading,
    refetch: refetchEsignLeaseAgreement,
  } = useEsignLeaseAgreement(user?.team!)
  const { staffUsers, isLoading: isStaffUsersLoading } = useStaffUsers()
  const { user: loggedInStaffUser, isLoading: isLoggedInUserLoading } = useLoggedInUser()

  function renderContent() {
    if (!team || isLeaseAgreementLoading || isStaffUsersLoading || isLoggedInUserLoading)
      return <InlineSpinner />

    if (!staffUsers || !esignLeaseAgreement || !loggedInStaffUser) return <>Something went wrong.</>

    return (
      <SignLeaseAgreementGenerator
        team={team}
        user={user}
        onClose={onClose}
        onSuccess={onSuccess}
        staffUsers={staffUsers}
        esignLeaseAgreement={esignLeaseAgreement}
        refetchEsignLeaseAgreement={refetchEsignLeaseAgreement}
        loggedInStaffUser={loggedInStaffUser}
        shouldTriggerSend={shouldTriggerSend}
      />
    )
  }

  return (
    <SimpleDialog onClose={onClose} isOpen alignText="left">
      <Wrap width="500px" maxWidth="100%">
        {renderContent()}
      </Wrap>
    </SimpleDialog>
  )
}

interface SignLeaseAgreementGeneratorProps {
  onClose: Handler
  onSuccess: () => Promise<any>
  team: Team
  user: User
  staffUsers: StaffUser[]
  loggedInStaffUser: any
  esignLeaseAgreement: EsignLeaseAgreement
  refetchEsignLeaseAgreement: () => Promise<any>
  shouldTriggerSend?: boolean
}

function SignLeaseAgreementGenerator({
  onClose,
  onSuccess,
  team,
  user,
  staffUsers,
  loggedInStaffUser,
  esignLeaseAgreement,
  refetchEsignLeaseAgreement,
  shouldTriggerSend,
}: SignLeaseAgreementGeneratorProps) {
  const [forceCreate, setForceCreate] = useState(false)
  const [salesOwner, setSalesOwner] = useState(loggedInStaffUser?.user?.id)
  const [isCreateLoading, setCreateIsLoading] = useState(false)
  const [isPreviewLoading, setPreviewIsLoading] = useState(false)
  const [isSentLoading, setSentIsLoading] = useState(false)
  const notify = useNotify()
  const truck = esignLeaseAgreement.trucks[0]

  const wrongRecipient = user.id !== esignLeaseAgreement?.defaultRecipient.id
  const canCreate = Boolean(
    team.legal_name_business &&
      team.business_type &&
      team.billing_address &&
      (team.has_tax_id || user.driver.hasSsn) &&
      user.shippingAddress &&
      truck &&
      !wrongRecipient
  )

  async function handleCreate() {
    setCreateIsLoading(true)
    try {
      await createEsignLeaseAgreement(team.id, { sales_owner: salesOwner })
      await refetchEsignLeaseAgreement()
      setForceCreate(false)
      if (!esignLeaseAgreement.hasDraft) {
        onSuccess()
      }
    } catch (err: any) {
      notify(`Oops! Something went wrong: ${JSON.stringify(err?.response?.data)}`, { type: 'error' })
    }
    setCreateIsLoading(false)
  }

  async function handlePreview() {
    setPreviewIsLoading(true)
    try {
      const url = await previewEsignLeaseAgreement(team.id, esignLeaseAgreement.envelope.id)
      window.open(url, '_blank')
    } catch (err: any) {
      notify(`Oops! Something went wrong: ${JSON.stringify(err?.response?.data)}`, { type: 'error' })
    }
    setPreviewIsLoading(false)
  }

  async function handleSent() {
    setSentIsLoading(true)
    try {
      await sendEsignLeaseAgreement(team.id, esignLeaseAgreement.envelope.id)
      await refetchEsignLeaseAgreement()
      onSuccess()
    } catch (err: any) {
      notify(`Oops! Something went wrong: ${JSON.stringify(err?.response?.data)}`, { type: 'error' })
    }
    setSentIsLoading(false)
  }

  useEffect(() => {
    if (shouldTriggerSend) handleSent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (esignLeaseAgreement.envelope && !forceCreate) {
    if (esignLeaseAgreement.envelope.status === EsignEnvelopeStatus.CREATED) {
      return (
        <>
          <Text variant="header2">Lease agreement ready</Text>
          <Box mt={2} />

          <Button variant="text" size="medium" onClick={handlePreview} isLoading={isPreviewLoading}>
            Preview
          </Button>

          <Box mt={5} />

          <Button
            variant="primary"
            size="large"
            onClick={handleSent}
            isLoading={isSentLoading}
            margin="24px auto"
          >
            Send for signing
          </Button>

          <Button variant="text" onClick={() => setForceCreate(true)} margin="24px auto">
            Generate new lease agreement
          </Button>
        </>
      )
    }

    if (
      [EsignEnvelopeStatus.DECLINED, EsignEnvelopeStatus.SIGNED].includes(esignLeaseAgreement.envelope.status)
    ) {
      return (
        <>
          <Text variant="header2">Lease agreement</Text>
          <Text variant="paragraph1" fontColor={midGray} mt={1}>
            Envelope is {esignLeaseAgreement.envelope.status.toLocaleLowerCase()}
          </Text>
        </>
      )
    }

    return null
  }

  return (
    <>
      <Text variant="header2">{esignLeaseAgreement?.hasDraft ? 'Generate' : 'Send'} lease agreement</Text>
      <Text variant="paragraph1" fontColor={midGray} mt={1}>
        This document will be legally binding between the fleet and CloudTrucks. If the information below is
        not correct, make the necessary edits before continuing.
      </Text>
      {!team.has_tax_id && !user.driver.hasSsn ? (
        <ErrorMessage mt={1} isSlim>
          <Text variant="header3">No Tax id or SSN</Text>
        </ErrorMessage>
      ) : null}
      {!truck ? (
        <ErrorMessage mt={1} isSlim>
          <Text variant="header3">
            No trucks with asssigned driver that has package 1 bg check completed.
          </Text>
        </ErrorMessage>
      ) : null}
      <Grid columns="180px 1fr" mt={3} gap="10px">
        <Text variant="label2" color={lightGray1}>
          Legal business name
        </Text>
        <Text variant="header3" fontColor={team.legal_name_business ? darkGray : errorD1}>
          {team.legal_name_business || <ErrorMessage isSlim>Missing!</ErrorMessage>}
        </Text>
        <Text variant="label2" color={lightGray1}>
          Type
        </Text>
        <Text variant="header3" fontColor={team.business_type ? darkGray : errorD1}>
          {team.business_type || <ErrorMessage isSlim>Missing!</ErrorMessage>}
        </Text>
        <Text variant="label2" color={lightGray1}>
          Owner
        </Text>
        <Text
          variant="header3"
          fontColor={esignLeaseAgreement?.defaultRecipient?.firstName ? darkGray : errorD1}
        >
          {esignLeaseAgreement?.defaultRecipient?.firstName} {esignLeaseAgreement?.defaultRecipient?.lastName}
        </Text>
        <Text variant="label2" color={lightGray1}>
          Fleet Address
        </Text>
        <Text variant="header3" fontColor={team.billing_address ? darkGray : errorD1}>
          {team.billing_address ? (
            <AddressFormat address={toCamelCase(team.billing_address)} />
          ) : (
            <ErrorMessage isSlim>Missing!</ErrorMessage>
          )}
        </Text>
        <Text variant="label2" color={lightGray1}>
          Owner Address
        </Text>
        <Text
          variant="header3"
          fontColor={esignLeaseAgreement?.defaultRecipient?.shippingAddress ? darkGray : errorD1}
        >
          {esignLeaseAgreement?.defaultRecipient?.shippingAddress ? (
            <AddressFormat address={esignLeaseAgreement?.defaultRecipient?.shippingAddress} />
          ) : (
            <ErrorMessage isSlim>Missing!</ErrorMessage>
          )}
        </Text>
        <Text variant="label2" color={lightGray1}>
          EIN
        </Text>
        <Text variant="header3" fontColor={darkGray}>
          <TeamTaxId record={team} hideHeader />
        </Text>
        <Text variant="label2" color={lightGray1}>
          Owner SSN
        </Text>
        <Text variant="header3" fontColor={darkGray}>
          <UserSSN record={toSnakeCase(esignLeaseAgreement?.defaultRecipient)} hideHeader />
        </Text>

        <Text variant="label2" color={lightGray1}>
          Truck
        </Text>
        <Text variant="header3" fontColor={truck ? darkGray : errorD1}>
          {truck ? (
            <>
              {truck.vehicleMake} {truck.vehicleModel} ({truck.vinNumber})
            </>
          ) : (
            <ErrorMessage isSlim>Missing!</ErrorMessage>
          )}
        </Text>

        <Text variant="label2" color={lightGray1} mt={1.5}>
          Sales owner
        </Text>
        <Text variant="header3" fontColor={darkGray}>
          <Dropdown
            value={salesOwner}
            onChange={setSalesOwner}
            options={orderBy(
              staffUsers.map((user) => ({ value: user.id, label: user.email })),
              ['label']
            )}
            maxHeight={300}
            noMargin
            isDisabled={isCreateLoading}
            backgroundColor={bgGray}
            position="top"
            emptyOptionsLabel="No users"
            addOptionsFilter
          />
        </Text>
      </Grid>
      <Box mt={3} />

      {canCreate && (
        <Button variant="primary" onClick={handleCreate} isLoading={isCreateLoading} margin="50px auto 0">
          {esignLeaseAgreement?.hasDraft ? 'Continue' : 'Send'}
        </Button>
      )}

      {wrongRecipient && (
        <Text variant="label2" color={midGray} mt={6}>
          You can genereate the lease agreement on the{' '}
          <Link to={`/users/${esignLeaseAgreement?.defaultRecipient?.id}/show#onboarding`}>
            {esignLeaseAgreement?.defaultRecipient?.firstName}{' '}
            {esignLeaseAgreement?.defaultRecipient?.lastName} profile.
          </Link>
        </Text>
      )}

      <Button variant="text" onClick={onClose} margin="24px auto">
        Cancel
      </Button>
    </>
  )
}

interface SignLeaseAgreementVoidModalProps {
  user: User
  onClose: Handler
  onSuccess: () => Promise<any>
}

function SignLeaseAgreementVoidModal({ onClose, onSuccess, user }: SignLeaseAgreementVoidModalProps) {
  const team = useTeam(user?.team)
  const {
    esignLeaseAgreement,
    isLoading: isLeaseAgreementLoading,
    refetch: refetchEsignLeaseAgreement,
  } = useEsignLeaseAgreement(user?.team!)

  function renderContent() {
    if (!team || isLeaseAgreementLoading) return <InlineSpinner />

    if (!esignLeaseAgreement) return <>Something went wrong.</>

    return (
      <SignLeaseAgreementVoid
        team={team}
        user={user}
        onClose={onClose}
        onSuccess={onSuccess}
        esignLeaseAgreement={esignLeaseAgreement}
        refetchEsignLeaseAgreement={refetchEsignLeaseAgreement}
      />
    )
  }

  return (
    <SimpleDialog onClose={onClose} isOpen alignText="left">
      <Wrap width="400px" maxWidth="100%">
        {renderContent()}
      </Wrap>
    </SimpleDialog>
  )
}

interface SignLeaseAgreementVoidProps {
  onClose: Handler
  onSuccess: () => Promise<any>
  team: Team
  user: User
  esignLeaseAgreement: EsignLeaseAgreement
  refetchEsignLeaseAgreement: () => Promise<any>
}

function SignLeaseAgreementVoid({
  onClose,
  onSuccess,
  team,
  esignLeaseAgreement,
  refetchEsignLeaseAgreement,
}: SignLeaseAgreementVoidProps) {
  const [reason, setReason] = useState('')
  const [isVoidLoading, setVoidIsLoading] = useState(false)
  const notify = useNotify()

  async function handleVoid() {
    if (!reason) return
    setVoidIsLoading(true)
    try {
      await voidEsignLeaseAgreement(team.id, esignLeaseAgreement.envelope.id, reason)
      await refetchEsignLeaseAgreement()
      onSuccess()
    } catch (err: any) {
      notify(`Oops! Something went wrong: ${JSON.stringify(err?.response?.data)}`, { type: 'error' })
    }
    setVoidIsLoading(false)
  }

  return (
    <>
      <Text variant="header2">Lease agreement</Text>
      <Text variant="paragraph1" fontColor={midGray} mt={1} mb={4}>
        Envelope has been already sent to the user
      </Text>

      <TextAreaInput
        onChange={(e) => setReason(e.target.value)}
        value={reason}
        backgroundColor={bgGray}
        rows={4}
        height="100px"
        label="Void reason"
      />

      <Button variant="primary" onClick={handleVoid} margin="24px auto" isLoading={isVoidLoading}>
        Void
      </Button>

      <Button variant="text" onClick={onClose} margin="24px auto">
        Cancel
      </Button>
    </>
  )
}
